import {
  AccountsRecievableStatementsHistoryState,
  AppRoute,
  AppRoutes,
  createOrderEntryHeader,
  getAccountReceivableStatementsURL,
  getCustomerSpecialOrders,
  getInvoicesURL,
  getQueryParamsString,
  InvoiceHistoryState,
  logUserActivity,
  OrderEntryQueryParams,
  RootState,
  RouteName,
  selectUserAreaSetting,
  selectUserFeatureSetting,
  selectUserPermissionSetting,
  setActiveOrderIsFutureSubmission,
  useAppDispatch,
  useAppSelector,
  UserActivityAction,
  UserActivityActionSummary,
  UserActivityPageName,
} from 'common';
import { customerSlice } from 'common/src/store/common/customer.slice';
import { selectAllProductShoppingCategories } from 'common/src/store/product-shopping-categories/product-shopping-category.slice';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CfDocumentViewerDialog } from '../../../../../../cf-ui/DocumentViewer/CfDocumentViewerDialog';
import { evaluateSwitchFromEditedOrder } from '../../../../../../hooks/orders/order-entry/useSwitchFromEditedOrder';
import { DeviceType, useBreakpoint } from '../../../../../../hooks/useBreakpoint';
import { getResolution } from '../../../../../../utilities/resolution';
import { Menus } from '../../hooks/useHeaderMenus';
import { NavigationBar } from './components/NavigationBar/NavigationBar';
import { NavigationDrawer } from './components/NavigationDrawer/NavigationDrawer';
import { NavButtonOptions, NavigationOptionVariant, NavigationProps, NavMenuConfig } from './NavigationProps';

export const Navigation: FC<NavigationProps> = (props: NavigationProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory<InvoiceHistoryState & AccountsRecievableStatementsHistoryState>();
  const { deviceType } = useBreakpoint();

  const userSite = useAppSelector((s: RootState) => s.user.userSite);
  const selectedCustomer = useAppSelector((s: RootState) => s.customer.selectedCustomer);

  const isOrderEntryEnabled = useAppSelector((s) => selectUserAreaSetting(s).IsOrderEntryEnabled);
  const isInvoicesEnabled = useAppSelector((s) => selectUserAreaSetting(s).IsInvoicesEnabled);
  const isInventoryEnabled = useAppSelector((s) => selectUserAreaSetting(s).IsParManagementEnabled);
  const isRecipesEnabled = useAppSelector((s) => selectUserAreaSetting(s).IsRecipesEnabled);

  const canViewInvoiceInsights = useAppSelector((s: RootState) => selectUserFeatureSetting(s).CanViewInvoiceInsights);
  const canViewParManagement = useAppSelector((s: RootState) => selectUserFeatureSetting(s).CanViewParManagement);
  const canViewThirdPartyVendors = useAppSelector(
    (s: RootState) => selectUserFeatureSetting(s).CanViewThirdPartyVendors
  );

  const canViewInventory = useAppSelector((s: RootState) => selectUserFeatureSetting(s).CanViewInventory);
  const canAccessInventoryManagement = useAppSelector(
    (s: RootState) => selectUserPermissionSetting(s).CanAccessInventoryManagement
  );
  const canCreateInventory = useAppSelector((s: RootState) => selectUserPermissionSetting(s).CanCreateInventory);
  const canViewInventoryPricing = useAppSelector(
    (s: RootState) => selectUserPermissionSetting(s).CanViewInventoryPricing
  );
  const canRunInventoryReports = useAppSelector(
    (s: RootState) => selectUserPermissionSetting(s).CanRunInventoryReports
  );

  const canViewInvoiceInsightsArea = useAppSelector((s) => selectUserPermissionSetting(s).CanViewInvoiceInsightsArea);
  const canAccessInvoiceArea = useAppSelector((s) => selectUserPermissionSetting(s).CanAccessInvoiceArea);
  const canAccessARStatementArea = useAppSelector((s) => selectUserPermissionSetting(s).CanAccessARStatementArea);
  const canAccessParManagement = useAppSelector((s) => selectUserPermissionSetting(s).CanAccessParManagement);
  const canAccessThirdPartyVendors = useAppSelector((s) => selectUserPermissionSetting(s).CanViewThirdPartyVendors);
  const canAccessListManagement = useAppSelector((s) => selectUserPermissionSetting(s).CanAccessListMangement);
  const canCreateOrder = useAppSelector((s) => selectUserPermissionSetting(s).CanCreateOrder);
  const canSendMessages = useAppSelector((s) => selectUserPermissionSetting(s).CanSendMessages);
  const canManageUserAccounts = useAppSelector((s) => selectUserPermissionSetting(s).CanManageUserAccounts);
  const canViewRecipes = useAppSelector((s) => selectUserPermissionSetting(s).CanViewRecipes);
  const hasSpecialOrderReport = useAppSelector((s: RootState) => s.customer.HasSpecialOrderReport);
  const shoppingCategories = useAppSelector(selectAllProductShoppingCategories);

  const recipesIsDisabled = !(isRecipesEnabled && canViewRecipes);
  const inventoryManagementIsDisabled = !canAccessInventoryManagement || !canViewInventory;
  const parManagementIsDisabled = !canAccessParManagement || !canViewParManagement;
  const vendorManagementIsDisabled = !canViewThirdPartyVendors || !canAccessThirdPartyVendors;
  const inventoryReportsIsDisabled =
    !canAccessInventoryManagement ||
    !canViewInventory ||
    (!canCreateInventory && (!canViewInventoryPricing || !canRunInventoryReports));

  const handleClickOption = ({ navigationAction, navigationRoute }: NavButtonOptions) => {
    if (history.location.pathname !== navigationRoute) {
      if (navigationRoute) {
        history.push(navigationRoute);
      } else {
        if (navigationAction) navigationAction();
      }
    }
  };

  const handleCreateNewOrderAction = () => {
    dispatch(setActiveOrderIsFutureSubmission(false));
    evaluateSwitchFromEditedOrder(() => {
      const appRoutes = Object.values(AppRoutes);
      const routeFrom = appRoutes.find(
        (appRoute: AppRoute) => appRoute.Path.toLocaleLowerCase() === location.pathname.toLocaleLowerCase()
      );
      dispatch(
        logUserActivity({
          action: UserActivityAction.CreateOrder,
          pageName: routeFrom?.UserActivityPageName || UserActivityPageName.Home,
          actionSummary: UserActivityActionSummary.CreateOrderFromHeader,
          resolution: getResolution(),
        })
      );
      dispatch(
        createOrderEntryHeader((orderId: string) => history.push(`${AppRoutes[RouteName.OrderEntry].Path}/${orderId}`))
      );
    });
  };

  const handleCreateNewItemOrderAction = () => {
    evaluateSwitchFromEditedOrder(() => {
      const appRoutes = Object.values(AppRoutes);
      const routeFrom = appRoutes.find(
        (appRoute: AppRoute) => appRoute.Path.toLocaleLowerCase() === location.pathname.toLocaleLowerCase()
      );
      dispatch(
        logUserActivity({
          action: UserActivityAction.CreateItemOrder,
          pageName: routeFrom?.UserActivityPageName || UserActivityPageName.Home,
          actionSummary: UserActivityActionSummary.CreateItemOrderFromHeader,
          resolution: getResolution(),
        })
      );
      dispatch(
        createOrderEntryHeader((orderId: string) =>
          history.push(
            `${AppRoutes[RouteName.OrderEntry].Path}/${orderId}` +
              getQueryParamsString({ itemOrderEntry: 'true' } as OrderEntryQueryParams)
          )
        )
      );
    });
  };

  const specialOrderFile = useAppSelector((s: RootState) => s.customer.specialOrderFile);
  const specialOrderFileLoading = useAppSelector((s: RootState) => s.customer.specialOrderFileLoading);
  const [specialOrderDialogOpen, setSpecialOrderDialogOpen] = useState(false);

  const handleSpecialOrderAction = async () => {
    if (!selectedCustomer) return;
    await dispatch(getCustomerSpecialOrders(selectedCustomer));
    setSpecialOrderDialogOpen(true);
  };

  let navigationConfiguration: NavMenuConfig[] = [];

  if (userSite && selectedCustomer) {
    const drawerOptions: NavMenuConfig[] =
      deviceType === DeviceType.Mobile
        ? [
            {
              label: 'My Account',
              testId: 'my-account-nav-btn',
              variant: NavigationOptionVariant.Menu,
              menuItems: [
                {
                  label: 'Profile',
                  testId: 'my-account-profile-nav-option',
                  navigationRoute: AppRoutes[RouteName.UserProfile].Path,
                },
                {
                  label: 'Manage users',
                  testId: 'my-account-manage-users-nav-option',
                  navigationRoute: AppRoutes[RouteName.ManageUsers].Path,
                  isDisabled: !canManageUserAccounts,
                },
                {
                  label: 'Communications',
                  testId: 'my-account-communication-nav-option',
                  navigationRoute: AppRoutes[RouteName.Communications].Path,
                },
                {
                  label: 'Message Center',
                  testId: 'my-account-messagecenter-nav-option',
                  navigationRoute: AppRoutes[RouteName.InboxMessages].Path,
                },
                {
                  label: 'Inbox',
                  testId: 'my-account-messagecenter-inbox-nav-option',
                  navigationRoute: AppRoutes[RouteName.InboxMessages].Path,
                  childOption: true,
                },
                {
                  label: 'Sent',
                  isDisabled: !canSendMessages,
                  testId: 'my-account-messagecenter-sent-nav-option',
                  navigationRoute: AppRoutes[RouteName.SentMessages].Path,
                  childOption: true,
                },
                {
                  label: 'Customer support',
                  testId: 'my-account-customersupport-nav-option',
                  navigationRoute: AppRoutes[RouteName.CustomerSupport].Path,
                },
              ],
            },
          ]
        : [];
    navigationConfiguration = [
      {
        label: 'Orders',
        isDisabled: !isOrderEntryEnabled && !isInvoicesEnabled,
        testId: 'orders-nav-btn',
        variant: NavigationOptionVariant.Menu,
        menuItems: [
          {
            label: 'Create new order',
            isDisabled: !canCreateOrder,
            testId: 'orders-createnew-nav-option',
            navigationAction: handleCreateNewOrderAction,
          },
          {
            label: 'Import order',
            isDisabled: !canCreateOrder,
            testId: 'orders-importorder-nav-option',
            navigationRoute: AppRoutes[RouteName.OrderImport].Path,
          },
          {
            label: 'Item order',
            isDisabled: !canCreateOrder,
            testId: 'orders-itemorder-nav-option',
            navigationAction: handleCreateNewItemOrderAction,
          },
          {
            label: 'Repeat order',
            isDisabled: !canCreateOrder,
            testId: 'orders-repeatorder-nav-option',
            navigationRoute: AppRoutes[RouteName.RepeatOrder].Path,
          },

          {
            label: 'Order history',
            isDisabled: !isOrderEntryEnabled && !isInvoicesEnabled,
            testId: 'orders-orderhistory-nav-option',
            navigationRoute: AppRoutes[RouteName.OrderHistory].Path,
          },
          {
            label: 'Smart order',
            isDisabled: !canCreateOrder || !isInventoryEnabled,
            testId: 'orders-smartorder-nav-option',
            navigationRoute: AppRoutes[RouteName.SmartOrder].Path,
          },
          ...(hasSpecialOrderReport
            ? [
                {
                  label: 'Special order report',
                  testId: 'orders-specialorder-nav-option',
                  navigationAction: handleSpecialOrderAction,
                },
              ]
            : []),
        ],
        menuHookValue: Menus.OrdersMenu,
      },
      {
        label: 'Invoices',
        isDisabled: !(
          canAccessInvoiceArea ||
          canAccessARStatementArea ||
          (canViewInvoiceInsights &&
            canViewInvoiceInsightsArea &&
            canAccessInvoiceArea &&
            deviceType !== DeviceType.Mobile)
        ),
        testId: 'invoices-nav-btn',
        variant: NavigationOptionVariant.Menu,
        menuItems: [
          {
            label: 'Invoices',
            testId: 'invoices-invoices-nav-option',
            isDisabled: !canAccessInvoiceArea,
            navigationAction: () => history.push(getInvoicesURL(undefined), { customers: [selectedCustomer] }),
          },
          {
            label: 'A/P statements',
            testId: 'invoices-arstatements-nav-option',
            isDisabled: !canAccessARStatementArea,
            navigationAction: () =>
              history.push(getAccountReceivableStatementsURL(undefined), { customers: [selectedCustomer] }),
          },
          {
            label: 'Invoice insights',
            testId: 'invoices-invoiceinsights-nav-option',
            isDisabled: !(
              canViewInvoiceInsights &&
              canViewInvoiceInsightsArea &&
              canAccessInvoiceArea &&
              deviceType !== DeviceType.Mobile
            ),
            navigationAction: () => history.push(AppRoutes[RouteName.Insights].Path),
          },
        ],
        menuHookValue: Menus.FinancesMenu,
      },
      {
        label: 'Lists',
        isDisabled: !canAccessListManagement,
        testId: 'list-mgmt-nav-btn',
        variant: NavigationOptionVariant.Button,
        navigationAction: () => {
          if (history.location.pathname !== AppRoutes[RouteName.ProductListManagement].Path)
            history.push(AppRoutes[RouteName.ProductListManagement].Path);
        },
      },
      {
        label: 'Inventory',
        isDisabled:
          inventoryManagementIsDisabled &&
          parManagementIsDisabled &&
          vendorManagementIsDisabled &&
          inventoryReportsIsDisabled,
        testId: 'inventory-nav-btn',
        variant: NavigationOptionVariant.Menu,
        menuItems: [
          {
            label: 'Inventory management',
            testId: 'inventory-management-nav-option',
            isDisabled: inventoryManagementIsDisabled,
            navigationRoute: AppRoutes[RouteName.InventoryManagement].Path,
          },
          {
            label: 'Par management',
            testId: 'inventory-par-management-nav-option',
            isDisabled: parManagementIsDisabled,
            navigationRoute: AppRoutes[RouteName.ParManagement].Path,
          },
          {
            label: 'Vendor management',
            testId: 'inventory-vendor-management-nav-option',
            isDisabled: vendorManagementIsDisabled,
            navigationRoute: AppRoutes[RouteName.VendorManagement].Path,
          },
          {
            label: 'Reports',
            testId: 'inventory-reports-nav-option',
            isDisabled: inventoryReportsIsDisabled,
            navigationRoute: AppRoutes[RouteName.InventoryReports].Path,
          },
        ],
        menuHookValue: Menus.InventoryMenu,
      },
      {
        label: 'Recipes',
        isDisabled: recipesIsDisabled,
        testId: 'receipes-nav-btn',
        variant: NavigationOptionVariant.Button,
        navigationAction: () => {
          if (history.location.pathname !== AppRoutes[RouteName.RecipeDashboard].Path)
            history.push(AppRoutes[RouteName.RecipeDashboard].Path);
        },
      },

      /*
        Corporate Recipes moved before SignOut - Will be decide where is the location.
         {
            label: 'Manage corporate ingredients',
            isDisabled: false,
            testId: 'recipes-manage-corporate-ingredients-nav-option',
            navigationRoute: AppRoutes[RouteName.CorporateIngredients].Path,
          },
          {
            label: 'Manage corporate ingredients categories',
            isDisabled: false,
            testId: 'recipes-manage-corporate-ingredients-categories-nav-option',
            navigationRoute: AppRoutes[RouteName.CorporateIngredientCategory].Path,
          },
          {
            label: 'Manage corporate recipes',
            isDisabled: false,
            testId: 'recipes-manage-corporate-nav-option',
            navigationRoute: AppRoutes[RouteName.CorporateRecipe].Path,
          },
      */
      // {
      //   label: 'Recipes',
      //   disabled: !userSite.BusinessUnitSetting.CanViewRecipes,
      //   testId: 'recipes-nav-btn',
      //   variant: NavigationOptionVariant.Menu,
      //   menuItems: [],
      // },
      // {
      //   label: 'Promotion',
      //   disabled: !userSite.BusinessUnitSetting.CanViewPromotionsAndRewards,
      //   testId: 'promos-rewards-nav-btn',
      //   variant: NavigationOptionVariant.Menu,
      //   menuItems: [],
      // },
      ...(props.variant === 'drawer' ? drawerOptions : []),
    ];
  }

  if (deviceType !== DeviceType.Desktop && shoppingCategories.length) {
    navigationConfiguration.unshift({
      label: 'Shop',
      testId: 'shop-nav-btn',
      variant: NavigationOptionVariant.Menu,
      isCollapsedByDefault: true,
      menuItems: shoppingCategories
        .filter((cat) => !cat.ParentProductShoppingCategoryId)
        .sort((a, b) => a.Sequence - b.Sequence)
        .map((cat) => {
          const childRoutes = shoppingCategories
            .filter((childCat) => cat.ProductShoppingCategoryId === childCat.ParentProductShoppingCategoryId)
            .sort((a, b) => a.Sequence - b.Sequence)
            .map((childCat) => ({
              label: childCat.Title,
              testId: `${childCat.Title}-nav-option`,
              isDisabled: false,
              navigationRoute: `${AppRoutes[RouteName.CategorySearch].Path}/${
                childCat.ParentProductShoppingCategoryId
              }?subcategoryId=${childCat.ProductShoppingCategoryId}`,
            }));

          if (childRoutes.length > 0) {
            childRoutes.unshift({
              label: `All ${cat.Title}`,
              testId: `all-${cat.Title}-nav-option`,
              isDisabled: false,
              navigationRoute: `${AppRoutes[RouteName.CategorySearch].Path}/${cat.ProductShoppingCategoryId}`,
            });
          }

          return {
            label: cat.Title,
            testId: `${cat.Title}-nav-option`,
            isDisabled: false,
            navigationRoute: `${AppRoutes[RouteName.CategorySearch].Path}/${cat.ProductShoppingCategoryId}`,
            isCollapsedByDefault: true,
            menuItems: childRoutes,
          };
        }),
    });
  }

  return (
    <>
      {props.variant === 'bar' ? (
        <NavigationBar navigationConfig={navigationConfiguration} onClickOption={handleClickOption} />
      ) : (
        <NavigationDrawer navigationConfig={navigationConfiguration} onClickOption={handleClickOption} />
      )}
      <CfDocumentViewerDialog
        isOpen={specialOrderDialogOpen}
        isLoading={specialOrderFileLoading}
        onClose={() => {
          setSpecialOrderDialogOpen(false);
          dispatch(customerSlice.actions.setSpecialOrderFile(undefined));
        }}
        fileResult={specialOrderFile}
      />
    </>
  );
};
